/**
 * @description Takes an input value on a range and maps it to a new range
 * @param value the number to remap, defaults to 0
 * @param rangeLow the low end of the range to remap from, defaults to 0
 * @param rangeHigh the high end of the range to remap from, defaults to 1
 * @param mapLow the low end of the range to remap to, defaults to 0
 * @param mapHigh the high end of the range to remap to, defaults to 100
 */
export function mapRange(
  value: number = 0,
  rangeLow: number = 0,
  rangeHigh: number = 1,
  mapLow: number = 0,
  mapHigh: number = 100
) {
  return ((value - rangeLow) * (mapHigh - mapLow)) / (rangeHigh - rangeLow) + mapLow;
}

/**
 * @description Clamps a value between a min and max
 * @param value the number to clamp, defaults to 0
 * @param min the minimum value, defaults to 0
 * @param max the maximum value, defaults to 100
 */
export function clamp(value: number = 0, min: number = 0, max: number = 100) {
  return Math.min(Math.max(value, min), max);
}

/**
 * @description Checks if a number is within a range
 * @param value the number to check
 * @param min the minimum value
 * @param max the maximum value
 */
export function isInRange(value: number, min: number, max: number) {
  return value >= min && value <= max;
}

/**
 * Checks if a value is a number or number-like string
 * @example
 * isNumeric(123)   // -> true
 * isNumeric('123') // -> true
 * isNumeric('abc') // -> false
 * isNumeric(NaN)   // -> false
 * isNumeric([])    // -> false
 */
export function isNumeric(value) {
  return !isNaN(parseFloat(value)) && isFinite(value);
}

/**
 * @description Checks if a value is strictly a number
 * @example
 * isNumber(123)   // -> true
 * isNumber('123') // -> false
 * isNumber('abc') // -> false
 * isNumber(NaN)   // -> false
 * isNumber([])    // -> false
 */
export function isNumber(value) {
  return typeof value === 'number' && isFinite(value);
}

/**
 * @description Converts a value to an integer with an optional rounding method
 * @param value the value to convert
 * @param rounding the rounding method to use (default: 'round')
 */
export function toInteger(value: any, rounding: 'round' | 'floor' | 'ceil' = 'round'): number {
  if (!isFinite(Number(value))) return 0;
  const n = Number(value);
  switch (rounding) {
    case 'floor':
      return Math.floor(n);
    case 'ceil':
      return Math.ceil(n);
    default:
      return Math.round(n);
  }
}

/**
 * @description Converts a value to a float with an optional precision level
 * @param value the value to convert
 * @param precision the precision level to use (default: 2)
 * @example
 * toFloat(123)             // -> 123
 * toFloat(123.123, 1)      // -> 123.1
 * toFloat(123, 0)          // -> 123
 * toFloat("123")           // -> 123
 * toFloat("123.123")       // -> 123.12
 * toFloat("123.199", 5)    // -> 123.12
 * toFloat("123.19999", 5)  // -> 123.19999
 * toFloat("123.199999", 5) // -> 123.2
 * toFloat('')              // -> 0
 * toFloat('John')          // -> 0
 * toFloat(NaN)             // -> 0
 * toFloat(null)            // -> 0
 */
export function toFloat(value: any, precision = 2): number {
  precision = clamp(precision, 0, 100); // .toFixed() will throw if precision is > 100
  return Number((isFinite(Number(value)) ? Number(value) : 0).toFixed(precision));
}

/**
 * @description simple global util to check if a value is even only for
 * readability instead of re-writing the modulo operator over and over
 * @param value value to check
 */
export const isEven = (value: number) => value % 2 === 0;

/**
 * @description simple global util to check if a value is odd only for
 * readability instead of re-writing the modulo operator over and over
 * @param value value to check
 */
export const isOdd = (value: number) => !isEven(value);

/**
 * @description get the nearest step values for a given value
 * @param value the value to get the nearest step values for
 * @param step the step to round to
 * @param min the minimum value
 * @param max the maximum value
 * @example
 * nearestFloatValues(1.777)          // -> [1, 2]
 * nearestFloatValues(1.5, 0.1, 0, 1) // -> [0.1, 0.2]
 * nearestFloatValues(2, 1, 0, 1)     // -> [1, 2]
 * nearestFloatValues(0.5, 0.1, 0, 1) // -> [0.5, 0.6]
 * nearestFloatValues2(0.75, 0.5)     // -> [0.75, 1]
 * @returns the nearest step values
 */
export function nearestFloatValues(value: number, step: number = 1, min: number = -Infinity, max: number = Infinity) {
  value = clamp(value, min, max);
  const precision = step.toString().split('.')[1]?.length || 0;
  const base = Math.floor(value / step) * step;
  let roundedDown = toFloat(clamp(base, min, max), precision);
  let roundedUp = toFloat(clamp(base + step, min, max), precision);

  // If we hit the max, adjust to return the last two valid steps
  if (roundedUp >= max) {
    roundedUp = toFloat(max, precision);
    roundedDown = toFloat(max - step, precision);
  }

  return [roundedDown, roundedUp];
}

/**
 * @description simple global util to get a random number between a min and max
 * @param min the minimum value
 * @param max the maximum value
 * @returns a random number between min and max
 */
export const randomBetween = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min + 1) + min);
};
